import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { BACKEND_URL } from '@/lib/api';
import {
  BarChart3, Package, ChefHat, FileSpreadsheet, Upload, Download,
  Plus, Search, X, Check, AlertTriangle, TrendingDown, DollarSign,
  Utensils, Wine, Coffee, PieChart
} from 'lucide-react';

const fmtCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return Number(v).toLocaleString('en-IN');
};

const TABS = [
  { id: 'daily', label: 'Daily Entry', icon: FileSpreadsheet },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'kitchens', label: 'Kitchens', icon: ChefHat },
  { id: 'nc', label: 'NC Entries', icon: Coffee },
  { id: 'items', label: 'Item Master', icon: Package },
  { id: 'recipes', label: 'BOM / Recipes', icon: Utensils },
];

export default function CostModule() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');

  return (
    <div className="max-w-7xl mx-auto space-y-4 pb-24" data-testid="cost-module">
      <div>
        <h1 className="text-xl font-bold" style={{ color: '#1B2A4A' }}>Cost Module — MindEscapes</h1>
        <p className="text-xs" style={{ color: '#6B7280' }}>Kitchen consumption, NC tracking, BOM/Recipes, and cost analytics</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto" data-testid="cost-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
            style={{
              backgroundColor: activeTab === t.id ? '#1B2A4A' : '#fff',
              color: activeTab === t.id ? '#fff' : '#6B7280',
              border: `1px solid ${activeTab === t.id ? '#1B2A4A' : '#E5E1DC'}`,
            }}
            data-testid={`cost-tab-${t.id}`}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'daily' && <DailyEntryForm />}
      {activeTab === 'dashboard' && <CostDashboard />}
      {activeTab === 'kitchens' && <KitchenConsumption />}
      {activeTab === 'nc' && <NCEntries />}
      {activeTab === 'items' && <ItemMaster />}
      {activeTab === 'recipes' && <RecipeModule />}
    </div>
  );
}


// ===================== DAILY ENTRY FORM (Saket's daily tracker) =====================
const ME_OUTLETS_SUB = {
  'Celeste By MindEscapes\u00ae (KETTI)': ['Breakfast', 'Divino', 'Xin Fine', 'Euphoria', 'Continental', 'Nirvana', 'Alfresco', 'Curated Experiences', 'Merchandise'],
  'Stays By MindEscapes\u00ae': ['Suite', 'Deluxe', 'Add-ons'],
  'ANMA By MindEscapes\u00ae (COONOOR)': ['Anma Cafe', 'Anma Xin', 'Anma Euphoria', 'Anma Nirvana', 'Merchandise'],
  'ANMA By MindEscapes\u00ae (OOTY)': ['Anma Cafe', 'Anma Xin', 'Anma Euphoria', 'Anma Nirvana', 'Merchandise'],
  'XIN By MindEscapes\u00ae HSR Layout': ['Bento', 'Calcutta Classics', 'Sides', 'Master Bowl', 'Beverages', 'My Bowl', 'Handheld', 'Soup', 'Zen', 'Euphoria Desserts', 'Curated Experience'],
  'XIN By MindEscapes\u00ae Koramangala': ['Xin Fine', 'Continental', 'Beverages', 'Euphoria', 'Curated Experience', 'Divino'],
};

const KITCHENS = ['Euphoria', 'Celeste', 'Nirvana', 'Xin Fine', 'Breakfast', 'Alfresco', 'Divino Liquor', 'Beverage'];

function DailyEntryForm() {
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + 330);
    return d.toISOString().split('T')[0];
  });
  const [outlet, setOutlet] = useState('');
  const [revenue, setRevenue] = useState({});
  const [kitchenCost, setKitchenCost] = useState({});
  const [covers, setCovers] = useState('');
  const [ncValue, setNcValue] = useState('');
  const [ncDetails, setNcDetails] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [incidents, setIncidents] = useState('');
  const [observations, setObservations] = useState('');
  const [reviewsPos, setReviewsPos] = useState('');
  const [reviewsNeg, setReviewsNeg] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [mgmtComments, setMgmtComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);

  const subCats = ME_OUTLETS_SUB[outlet] || [];
  const totalRev = Object.values(revenue).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const totalCost = Object.values(kitchenCost).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const avgCheck = covers > 0 ? Math.round(totalRev / parseInt(covers)) : 0;
  const costPct = totalRev > 0 ? (totalCost / totalRev * 100).toFixed(1) : '0';

  useEffect(() => {
    api.get(`/cost/daily-entries?date_from=${date}&date_to=${date}`).then(r => setEntries(r.data.entries)).catch(() => {});
  }, [date]);

  const submit = async () => {
    if (!date || !outlet) return;
    setSaving(true);
    try {
      await api.post('/cost/daily-entry', {
        date, outlet,
        revenue_breakdown: revenue,
        total_revenue: totalRev,
        covers: parseInt(covers) || 0,
        avg_check: avgCheck,
        kitchen_consumption: kitchenCost,
        total_consumption: totalCost,
        nc_value: parseFloat(ncValue) || 0,
        nc_details: ncDetails,
        discount_value: parseFloat(discountValue) || 0,
        discount_reason: discountReason,
        incidents, observations,
        reviews_positive: parseInt(reviewsPos) || 0,
        reviews_negative: parseInt(reviewsNeg) || 0,
        review_notes: reviewNotes,
        management_comments: mgmtComments,
      });
      setRevenue({}); setKitchenCost({}); setCovers(''); setNcValue(''); setNcDetails('');
      setDiscountValue(''); setDiscountReason(''); setIncidents(''); setObservations('');
      setReviewsPos(''); setReviewsNeg(''); setReviewNotes(''); setMgmtComments('');
      api.get(`/cost/daily-entries?date_from=${date}&date_to=${date}`).then(r => setEntries(r.data.entries)).catch(() => {});
    } catch (err) { alert(err?.response?.data?.detail || 'Error saving'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="daily-entry-form">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Daily Cost Entry</h3>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} data-testid="daily-date" />
          <select value={outlet} onChange={e => { setOutlet(e.target.value); setRevenue({}); }}
            className="text-xs border rounded px-2 py-1.5 min-w-[220px]" style={{ borderColor: '#E5E1DC' }} data-testid="daily-outlet">
            <option value="">Select outlet...</option>
            {Object.keys(ME_OUTLETS_SUB).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {outlet && (
        <div className="space-y-4">
          {/* Section 1: Revenue by sub-category */}
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Sales & Revenue</h4>
              <span className="text-sm font-bold" style={{ color: '#2A7B88' }}>Total: {fmtCurrency(totalRev)}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {subCats.map(sc => (
                <div key={sc}>
                  <label className="text-[10px]" style={{ color: '#6B7280' }}>{sc}</label>
                  <input type="number" value={revenue[sc] || ''} onChange={e => setRevenue({ ...revenue, [sc]: e.target.value })}
                    placeholder="0" className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Section 2: Kitchen Consumption */}
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Kitchen Consumption</h4>
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold" style={{ color: totalCost > 0 ? '#D4726A' : '#9CA3AF' }}>Cost: {fmtCurrency(totalCost)}</span>
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: parseFloat(costPct) > 35 ? '#FEF2F2' : parseFloat(costPct) > 30 ? '#FEF3C7' : '#ECFDF5', color: parseFloat(costPct) > 35 ? '#D4726A' : parseFloat(costPct) > 30 ? '#BA7517' : '#2D8B5E' }}>
                  {costPct}%
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {KITCHENS.map(k => (
                <div key={k}>
                  <label className="text-[10px]" style={{ color: '#6B7280' }}>{k}</label>
                  <input type="number" value={kitchenCost[k] || ''} onChange={e => setKitchenCost({ ...kitchenCost, [k]: e.target.value })}
                    placeholder="0" className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Operational Metrics + NC + Discount */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Operations</h4>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px]" style={{ color: '#6B7280' }}>Covers</label>
                  <input type="number" value={covers} onChange={e => setCovers(e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
                </div>
                {covers > 0 && <p className="text-[10px]" style={{ color: '#6B7280' }}>Avg check: {fmtCurrency(avgCheck)}</p>}
              </div>
            </div>
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>NC (Non-Chargeable)</h4>
              <input type="number" value={ncValue} onChange={e => setNcValue(e.target.value)}
                placeholder="Amount" className="w-full text-xs border rounded px-2 py-1.5 mb-1" style={{ borderColor: '#E5E1DC' }} />
              <input value={ncDetails} onChange={e => setNcDetails(e.target.value)}
                placeholder="Details..." className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Discount</h4>
              <input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                placeholder="Amount" className="w-full text-xs border rounded px-2 py-1.5 mb-1" style={{ borderColor: '#E5E1DC' }} />
              <input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                placeholder="Reason..." className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>

          {/* Section 4: Incidents, Reviews, Comments */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Incident / Observation</h4>
              <textarea value={incidents} onChange={e => setIncidents(e.target.value)}
                placeholder="Any incidents..." className="w-full text-xs border rounded px-2 py-1.5 h-16" style={{ borderColor: '#E5E1DC' }} />
              <textarea value={observations} onChange={e => setObservations(e.target.value)}
                placeholder="Observations..." className="w-full text-xs border rounded px-2 py-1.5 h-16 mt-1" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Reviews</h4>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px]" style={{ color: '#2D8B5E' }}>Positive</label>
                  <input type="number" value={reviewsPos} onChange={e => setReviewsPos(e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
                <div><label className="text-[10px]" style={{ color: '#D4726A' }}>Negative</label>
                  <input type="number" value={reviewsNeg} onChange={e => setReviewsNeg(e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} /></div>
              </div>
              <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                placeholder="Review highlights..." className="w-full text-xs border rounded px-2 py-1.5 h-12 mt-1" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Management Comments</h4>
              <textarea value={mgmtComments} onChange={e => setMgmtComments(e.target.value)}
                placeholder="Comments..." className="w-full text-xs border rounded px-2 py-1.5 h-24" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>

          <button onClick={submit} disabled={saving || !date || !outlet}
            className="px-6 py-2.5 rounded-md text-sm font-medium text-white disabled:opacity-40"
            style={{ backgroundColor: '#1B2A4A' }} data-testid="submit-daily-entry">
            {saving ? 'Saving...' : 'Submit Daily Entry'}
          </button>
        </div>
      )}

      {/* Today's entries */}
      {entries.length > 0 && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
          <h4 className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Entries for {date}</h4>
          <div className="space-y-2">
            {entries.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{e.outlet}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Covers: {e.covers} | NC: {fmtCurrency(e.nc_value)} | By: {e.entered_by}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold" style={{ color: '#2A7B88' }}>{fmtCurrency(e.total_revenue)}</p>
                  <p className="text-[10px]" style={{ color: e.daily_cost_pct > 35 ? '#D4726A' : '#2D8B5E' }}>Cost: {e.daily_cost_pct}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ===================== COST DASHBOARD =====================
function CostDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    const [y, m] = period.split('-');
    const start = `${y}-${m}-01`;
    const end = new Date(Number(y), Number(m), 0).toISOString().split('T')[0];
    api.get(`/cost/dashboard?period_start=${start}&period_end=${end}`).then(r => setData(r.data)).catch(() => {});
  }, [period]);

  if (!data) return <p className="text-xs py-8 text-center" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const cards = [
    { label: 'Revenue Base', value: fmtCurrency(data.revenue_base), color: '#2A7B88' },
    { label: 'Kitchen Cost', value: fmtCurrency(data.total_kitchen_consumption), sub: `${data.kitchen_cost_pct}%`, color: data.kitchen_cost_pct > 40 ? '#D4726A' : '#2D8B5E' },
    { label: 'NC Total', value: fmtCurrency(data.total_nc), sub: `${data.nc_pct}%`, color: '#BA7517' },
    { label: 'Net F&B Cost', value: fmtCurrency(data.net_fnb_consumption), sub: `${data.net_fnb_pct}%`, color: data.net_fnb_pct > 35 ? '#D4726A' : '#2D8B5E' },
  ];

  return (
    <div className="space-y-4" data-testid="cost-dashboard">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Period</h3>
        <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
          className="text-xs border rounded px-2 py-1" style={{ borderColor: '#E5E1DC' }} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>{c.label}</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{c.value}</p>
            {c.sub && <p className="text-xs font-semibold" style={{ color: c.color }}>{c.sub} of revenue</p>}
          </div>
        ))}
      </div>

      {/* Kitchen Breakdown */}
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
        <h4 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>Kitchen Breakdown</h4>
        {data.kitchen_breakdown.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <th className="text-left px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Kitchen</th>
                  <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Consumption</th>
                  <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Revenue</th>
                  <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Cost %</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {data.kitchen_breakdown.map(k => (
                  <tr key={k.kitchen}>
                    <td className="px-2 py-2 font-medium" style={{ color: '#1B2A4A' }}>{k.kitchen}</td>
                    <td className="px-2 py-2 text-right">{fmtCurrency(k.consumption)}</td>
                    <td className="px-2 py-2 text-right">{fmtCurrency(k.revenue)}</td>
                    <td className="px-2 py-2 text-right">
                      <span className="font-semibold px-1.5 py-0.5 rounded-full text-[10px]"
                        style={{
                          backgroundColor: k.gross_cost_pct > 40 ? '#FEF2F2' : k.gross_cost_pct > 30 ? '#FEF3C7' : '#ECFDF5',
                          color: k.gross_cost_pct > 40 ? '#D4726A' : k.gross_cost_pct > 30 ? '#BA7517' : '#2D8B5E',
                        }}>
                        {k.gross_cost_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-center py-6" style={{ color: '#9CA3AF' }}>No kitchen data yet. Add consumption entries or import historical data.</p>
        )}
      </div>

      {/* Variance Alerts */}
      {data.variance_alerts.length > 0 && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#D4726A', borderWidth: '1.5px' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#D4726A' }} />
            <h4 className="text-sm font-semibold" style={{ color: '#D4726A' }}>Cost Alerts</h4>
          </div>
          {data.variance_alerts.map((a, i) => (
            <p key={i} className="text-xs py-1" style={{ color: '#1B2A4A' }}>{a.message}</p>
          ))}
        </div>
      )}

      <div className="flex gap-3 text-[10px]" style={{ color: '#9CA3AF' }}>
        <span>Items in master: {data.item_count}</span>
        <span>Recipes loaded: {data.recipe_count}</span>
      </div>
    </div>
  );
}


// ===================== KITCHEN CONSUMPTION =====================
function KitchenConsumption() {
  const [kitchens, setKitchens] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kitchen: '', period_start: '', period_end: '', consumption_value: '', revenue: '', notes: '' });
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/cost/kitchens').then(r => setKitchens(r.data.kitchens)).catch(() => {});
    api.get('/cost/kitchen-consumption').then(r => setEntries(r.data.entries)).catch(() => {});
  }, []);

  const refresh = () => api.get('/cost/kitchen-consumption').then(r => setEntries(r.data.entries)).catch(() => {});

  const submit = async () => {
    try {
      await api.post('/cost/kitchen-consumption', {
        ...form,
        consumption_value: parseFloat(form.consumption_value) || 0,
        revenue: parseFloat(form.revenue) || 0,
        period_type: 'monthly',
      });
      setShowForm(false);
      setForm({ kitchen: '', period_start: '', period_end: '', consumption_value: '', revenue: '', notes: '' });
      refresh();
    } catch (err) { alert(err?.response?.data?.detail || 'Error'); }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/cost/import-historical', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      refresh();
    } catch (err) { setImportResult({ error: err?.response?.data?.detail || 'Import failed' }); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.delete(`/cost/kitchen-consumption/${id}`); refresh(); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="kitchen-consumption">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Kitchen Consumption Entries</h3>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/cost/kitchens/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-kitchens">
            <Download size={12} /> Export CSV
          </a>
          <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="import-historical-btn">
            <Upload size={12} /> Import Historical
          </button>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="add-consumption-btn">
            <Plus size={12} /> Add Entry
          </button>
        </div>
      </div>

      {showImport && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#2A7B88' }}>
          <p className="text-xs mb-2" style={{ color: '#6B7280' }}>CSV format: kitchen, period_start, period_end, consumption_value, revenue</p>
          <input ref={fileRef} type="file" accept=".csv" onChange={e => handleImport(e.target.files?.[0])}
            className="text-xs" data-testid="historical-csv-input" />
          {importResult && !importResult.error && <p className="text-xs mt-2" style={{ color: '#2D8B5E' }}>Imported {importResult.imported} entries</p>}
          {importResult?.error && <p className="text-xs mt-2" style={{ color: '#D4726A' }}>{importResult.error}</p>}
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Kitchen</label>
              <select value={form.kitchen} onChange={e => setForm({ ...form, kitchen: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                <option value="">Select kitchen...</option>
                {kitchens.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Period Start</label>
              <input type="date" value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Period End</label>
              <input type="date" value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Consumption Value (INR)</label>
              <input type="number" value={form.consumption_value} onChange={e => setForm({ ...form, consumption_value: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} placeholder="e.g. 81390" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Revenue (INR)</label>
              <input type="number" value={form.revenue} onChange={e => setForm({ ...form, revenue: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} placeholder="e.g. 304167" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>
          <button onClick={submit} disabled={!form.kitchen || !form.period_start}
            className="px-4 py-2 rounded-md text-xs font-medium text-white disabled:opacity-40"
            style={{ backgroundColor: '#2A7B88' }}>Save Entry</button>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Kitchen</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Period</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Consumption</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Revenue</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Cost %</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Entered By</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {entries.map((e, i) => (
              <tr key={i}>
                <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{e.kitchen}</td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.period_start} — {e.period_end}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(e.consumption_value)}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(e.revenue)}</td>
                <td className="px-3 py-2 text-right">
                  <span className="font-semibold px-1.5 py-0.5 rounded-full text-[10px]"
                    style={{
                      backgroundColor: e.gross_cost_pct > 40 ? '#FEF2F2' : e.gross_cost_pct > 30 ? '#FEF3C7' : '#ECFDF5',
                      color: e.gross_cost_pct > 40 ? '#D4726A' : e.gross_cost_pct > 30 ? '#BA7517' : '#2D8B5E',
                    }}>
                    {e.gross_cost_pct}%
                  </span>
                </td>
                <td className="px-3 py-2" style={{ color: '#9CA3AF' }}>{e.entered_by}</td>
                <td className="px-3 py-2 text-right">
                  {e.id && <button onClick={() => deleteEntry(e.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 transition-all"
                    style={{ color: '#D4726A' }}>Delete</button>}
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>No entries yet. Add consumption data or import historical.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===================== NC ENTRIES =====================
function NCEntries() {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', person: '', nc_type: '', food_value: '', beverage_value: '', liquor_value: '', allowed_limit: '', approved_by: '', notes: '' });

  const NC_TYPES = ['Operations Staff', 'Complementary', 'Tasting & Trial', 'Breakfast', 'Photoshoot', 'DJ', 'Inter Department', 'Business Promotion', 'Room Complementary', 'Guest Complementary', 'Spoilage', 'Other'];

  useEffect(() => {
    api.get('/cost/nc').then(r => setEntries(r.data.entries)).catch(() => {});
  }, []);

  const refresh = () => api.get('/cost/nc').then(r => setEntries(r.data.entries)).catch(() => {});

  const submit = async () => {
    try {
      await api.post('/cost/nc', {
        ...form,
        food_value: parseFloat(form.food_value) || 0,
        beverage_value: parseFloat(form.beverage_value) || 0,
        liquor_value: parseFloat(form.liquor_value) || 0,
        allowed_limit: parseFloat(form.allowed_limit) || 0,
      });
      setShowForm(false);
      setForm({ date: '', person: '', nc_type: '', food_value: '', beverage_value: '', liquor_value: '', allowed_limit: '', approved_by: '', notes: '' });
      refresh();
    } catch (err) { alert(err?.response?.data?.detail || 'Error'); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this NC entry?')) return;
    try { await api.delete(`/cost/nc/${id}`); refresh(); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="nc-entries">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Non-Chargeable Entries</h3>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/cost/nc/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-nc">
            <Download size={12} /> Export CSV
          </a>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }} data-testid="add-nc-btn">
            <Plus size={12} /> Add NC Entry
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Person</label>
              <input value={form.person} onChange={e => setForm({ ...form, person: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} placeholder="e.g. Dipali Sikand" />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>NC Type</label>
              <select value={form.nc_type} onChange={e => setForm({ ...form, nc_type: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                <option value="">Select type...</option>
                {NC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Approved By</label>
              <input value={form.approved_by} onChange={e => setForm({ ...form, approved_by: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Food Value (INR)</label>
              <input type="number" value={form.food_value} onChange={e => setForm({ ...form, food_value: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Beverage Value</label>
              <input type="number" value={form.beverage_value} onChange={e => setForm({ ...form, beverage_value: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Liquor Value</label>
              <input type="number" value={form.liquor_value} onChange={e => setForm({ ...form, liquor_value: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div>
              <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Allowed Limit</label>
              <input type="number" value={form.allowed_limit} onChange={e => setForm({ ...form, allowed_limit: e.target.value })}
                className="w-full text-xs border rounded px-2 py-1.5 mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>
          <button onClick={submit} disabled={!form.date || !form.person}
            className="px-4 py-2 rounded-md text-xs font-medium text-white disabled:opacity-40"
            style={{ backgroundColor: '#2A7B88' }}>Save NC Entry</button>
        </div>
      )}

      <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Date</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Person</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Type</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Food</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Bev</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Liquor</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Total</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {entries.map((e, i) => (
              <tr key={i}>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.date}</td>
                <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{e.person}</td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{e.nc_type}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(e.food_value)}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(e.beverage_value)}</td>
                <td className="px-3 py-2 text-right">{fmtCurrency(e.liquor_value)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtCurrency(e.total_value)}</td>
                <td className="px-3 py-2 text-right">
                  {e.id && <button onClick={() => deleteEntry(e.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 transition-all"
                    style={{ color: '#D4726A' }}>Delete</button>}
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>No NC entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===================== ITEM MASTER =====================
function ItemMaster() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [form, setForm] = useState({ name: '', uom: 'KG', cost_price: '', category: 'Groceries', supplier: '' });
  const fileRef = useRef(null);

  const fetchItems = (q = '') => {
    api.get(`/cost/items?search=${q}&limit=1000`).then(r => { setItems(r.data.items); setTotal(r.data.total); }).catch(() => {});
  };

  useEffect(() => { fetchItems(); }, []);

  const submit = async () => {
    try {
      await api.post('/cost/items', { ...form, cost_price: parseFloat(form.cost_price) || 0 });
      setShowForm(false);
      setForm({ name: '', uom: 'KG', cost_price: '', category: 'Groceries', supplier: '' });
      fetchItems(search);
    } catch (err) { alert(err?.response?.data?.detail || 'Error'); }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/cost/items/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      fetchItems(search);
    } catch (err) { setImportResult({ error: err?.response?.data?.detail || 'Import failed' }); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteItem = async (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/cost/items/${encodeURIComponent(name)}`); fetchItems(search); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="item-master">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Item Master ({total} items)</h3>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/cost/items/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-items">
            <Download size={12} /> Export CSV
          </a>
          <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Upload size={12} /> Import CSV
          </button>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }}>
            <Plus size={12} /> Add Item
          </button>
        </div>
      </div>

      {showImport && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#2A7B88' }}>
          <p className="text-xs mb-2" style={{ color: '#6B7280' }}>CSV: name, uom, cost_price, category, supplier (or eZee BOM format: MenuItemName, InventoryItemName, UnitName, Quantity, CostPerUnit)</p>
          <input ref={fileRef} type="file" accept=".csv" onChange={e => handleImport(e.target.files?.[0])} className="text-xs" />
          {importResult && !importResult.error && <p className="text-xs mt-2" style={{ color: '#2D8B5E' }}>Imported {importResult.imported}, skipped {importResult.skipped}</p>}
          {importResult?.error && <p className="text-xs mt-2" style={{ color: '#D4726A' }}>{importResult.error}</p>}
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-2 top-2" style={{ color: '#9CA3AF' }} />
          <input value={search} onChange={e => { setSearch(e.target.value); fetchItems(e.target.value); }}
            placeholder="Search items..." className="w-full text-xs border rounded pl-7 pr-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Item name" className="text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
            <select value={form.uom} onChange={e => setForm({ ...form, uom: e.target.value })}
              className="text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }}>
              {['KG', 'Ltr', 'ML', 'GRMS', 'PC', 'Packet', 'Tin', 'Bottle', 'Box'].map(u => <option key={u}>{u}</option>)}
            </select>
            <input type="number" value={form.cost_price} onChange={e => setForm({ ...form, cost_price: e.target.value })}
              placeholder="Cost/unit" className="text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              placeholder="Category" className="text-xs border rounded px-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
            <button onClick={submit} disabled={!form.name} className="px-3 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-40"
              style={{ backgroundColor: '#2A7B88' }}>Add</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Item Name</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>UOM</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Cost/Unit</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Category</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {items.map((item, i) => (
              <tr key={i}>
                <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{item.name}</td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{item.uom}</td>
                <td className="px-3 py-2 text-right">{item.cost_price > 0 ? `${item.cost_price}` : '—'}</td>
                <td className="px-3 py-2" style={{ color: '#6B7280' }}>{item.category}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deleteItem(item.name)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 transition-all"
                    style={{ color: '#D4726A' }}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-8 text-center" style={{ color: '#9CA3AF' }}>No items. Import from eZee BOM or add manually.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===================== RECIPE / BOM MODULE =====================
function RecipeModule() {
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef(null);

  const fetchRecipes = (q = '') => {
    api.get(`/cost/recipes?search=${q}`).then(r => setRecipes(r.data.recipes)).catch(() => {});
  };

  useEffect(() => { fetchRecipes(); }, []);

  const handleImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/cost/recipes/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      fetchRecipes(search);
    } catch (err) { setImportResult({ error: err?.response?.data?.detail || 'Import failed' }); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteRecipe = async (name) => {
    if (!window.confirm(`Delete recipe "${name}"?`)) return;
    try { await api.delete(`/cost/recipes/${encodeURIComponent(name)}`); setSelected(null); fetchRecipes(search); } catch {}
  };

  return (
    <div className="space-y-4" data-testid="recipe-module">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>BOM / Recipes ({recipes.length})</h3>
        <div className="flex gap-2">
          <a href={`${BACKEND_URL}/api/cost/recipes/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-recipes">
            <Download size={12} /> Export CSV
          </a>
          <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
            style={{ backgroundColor: '#2A7B88' }}>
            <Upload size={12} /> Import BOM (CSV)
          </button>
        </div>
      </div>

      {showImport && (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#2A7B88' }}>
          <p className="text-xs mb-2" style={{ color: '#6B7280' }}>eZee BOM format: MenuItemName, InventoryItemName, UnitName, Quantity, CostPerUnit</p>
          <input ref={fileRef} type="file" accept=".csv" onChange={e => handleImport(e.target.files?.[0])} className="text-xs" />
          {importResult && !importResult.error && (
            <p className="text-xs mt-2" style={{ color: '#2D8B5E' }}>
              Imported {importResult.imported} new, updated {importResult.updated}. Total: {importResult.total_recipes} recipes.
            </p>
          )}
          {importResult?.error && <p className="text-xs mt-2" style={{ color: '#D4726A' }}>{importResult.error}</p>}
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-2 top-2" style={{ color: '#9CA3AF' }} />
          <input value={search} onChange={e => { setSearch(e.target.value); fetchRecipes(e.target.value); }}
            placeholder="Search recipes..." className="w-full text-xs border rounded pl-7 pr-2 py-1.5" style={{ borderColor: '#E5E1DC' }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Recipe list */}
        <div className="bg-white rounded-lg border overflow-y-auto" style={{ borderColor: '#E5E1DC', maxHeight: 500 }}>
          {recipes.map((r, i) => (
            <div key={i} onClick={() => setSelected(r)}
              className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 border-b"
              style={{ borderColor: '#F3F4F6', backgroundColor: selected?.name === r.name ? '#E6F1FB' : 'transparent' }}>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{r.name}</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{r.ingredients?.length || 0} ingredients · {r.kitchen || r.outlet || '—'}</p>
              </div>
              <div className="text-right shrink-0 ml-2 flex items-center gap-2">
                <div>
                  <p className="text-xs font-bold" style={{ color: '#2A7B88' }}>{r.theoretical_cost > 0 ? `${r.theoretical_cost.toFixed(0)}` : '—'}</p>
                  {r.food_cost_pct > 0 && <p className="text-[10px]" style={{ color: '#6B7280' }}>{r.food_cost_pct}% FC</p>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteRecipe(r.name); }}
                  className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 transition-all" style={{ color: '#D4726A' }}>X</button>
              </div>
            </div>
          ))}
          {recipes.length === 0 && (
            <p className="text-xs text-center py-8" style={{ color: '#9CA3AF' }}>No recipes loaded. Import from eZee BOM CSV.</p>
          )}
        </div>

        {/* Recipe detail */}
        {selected ? (
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
            <h4 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{selected.name}</h4>
            <div className="flex gap-4 mt-1 mb-3 text-[10px]" style={{ color: '#6B7280' }}>
              <span>Kitchen: {selected.kitchen || '—'}</span>
              <span>Cost: {selected.theoretical_cost?.toFixed(2)}</span>
              {selected.selling_price > 0 && <span>Price: {selected.selling_price}</span>}
              {selected.food_cost_pct > 0 && <span>FC%: {selected.food_cost_pct}%</span>}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <th className="text-left px-2 py-1 font-medium" style={{ color: '#6B7280' }}>Ingredient</th>
                  <th className="text-right px-2 py-1 font-medium" style={{ color: '#6B7280' }}>Qty</th>
                  <th className="text-left px-2 py-1 font-medium" style={{ color: '#6B7280' }}>UOM</th>
                  <th className="text-right px-2 py-1 font-medium" style={{ color: '#6B7280' }}>Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {(selected.ingredients || []).map((ing, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1" style={{ color: '#1B2A4A' }}>{ing.item_name}</td>
                    <td className="px-2 py-1 text-right">{ing.quantity || '—'}</td>
                    <td className="px-2 py-1" style={{ color: '#6B7280' }}>{ing.uom}</td>
                    <td className="px-2 py-1 text-right">{ing.line_cost > 0 ? ing.line_cost.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border p-4 flex items-center justify-center" style={{ borderColor: '#E5E1DC', minHeight: 200 }}>
            <p className="text-xs" style={{ color: '#9CA3AF' }}>Select a recipe to view ingredients</p>
          </div>
        )}
      </div>
    </div>
  );
}
